import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { locale, loadMessages } from 'devextreme/localization';
import esMessages from 'devextreme/localization/messages/es.json';
import enMessages from 'devextreme/localization/messages/en.json';
import ptMessages from 'devextreme/localization/messages/pt.json';

export type AppLang = 'es' | 'en' | 'pt';

const LS_KEY = 'lang';
const SUPPORTED: AppLang[] = ['es', 'en', 'pt'];
const DEFAULT_LANG: AppLang = 'es';

/**
 * Fuente de verdad del idioma activo. Maneja simultáneamente:
 *  1. ngx-translate (textos estáticos del frontend)
 *  2. DevExtreme `locale()` (paginador, calendarios, filtros, formatos)
 *  3. localStorage para persistir entre sesiones
 *
 * Para captions del backend (textos dinámicos), ver `AcceptLanguageInterceptor`
 * que adjunta el header `Accept-Language` a cada request — el backend resuelve
 * con `IStringLocalizer<T>` (.resx).
 *
 * Diseño acordado en docs/migration/_i18n.md.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translate = inject(TranslateService);

  private readonly _lang$ = new BehaviorSubject<AppLang>(this.readInitialLang());
  /** Observable suscribible — los componentes con captions del backend deben
   *  reaccionar para refetchear el `DataSource`. */
  readonly lang$ = this._lang$.asObservable();

  /** Snapshot síncrono (para servicios que no quieren suscribirse). */
  get current(): AppLang { return this._lang$.value; }

  constructor() {
    // Cargamos los 3 diccionarios de DevExtreme una sola vez al instanciar.
    loadMessages(esMessages);
    loadMessages(enMessages);
    loadMessages(ptMessages);

    // Configuración inicial de ngx-translate.
    this.translate.addLangs(SUPPORTED);
    this.translate.setDefaultLang(DEFAULT_LANG);

    // Aplicamos el idioma persistido (o default) en ambos sistemas.
    this.cambiarIdioma(this.current);
  }

  /**
   * Cambia el idioma activo. Sincroniza ngx-translate, DevExtreme y localStorage.
   *
   * Orden:
   *  1. Persistimos en localStorage YA — así el `AcceptLanguageInterceptor`
   *     que lee de localStorage envía el header correcto en la PRIMERA request
   *     que vuele junto con el cambio.
   *  2. Disparamos `translate.use(lang)` y nos suscribimos (sin suscripción el
   *     http-loader es cold y no dispara la carga del JSON).
   *  3. Cuando el JSON cargó, sincronizamos DevExtreme `locale()` y emitimos
   *     `lang$` para que los componentes con captions del backend refresquen.
   *
   * Los pipes `| translate` muestran la clave literal mientras el JSON está
   * cargando — luego ngx-translate re-evalúa y muestra el texto traducido.
   */
  cambiarIdioma(lang: AppLang): void {
    if (!SUPPORTED.includes(lang)) {
      console.warn(`[LanguageService] idioma no soportado: ${lang}, usando ${DEFAULT_LANG}`);
      lang = DEFAULT_LANG;
    }
    try { localStorage.setItem(LS_KEY, lang); } catch { /* localStorage bloqueado */ }
    this.translate.use(lang).subscribe({
      next: () => {
        locale(lang);
        this._lang$.next(lang);
      },
      error: (err) => {
        console.error(`[LanguageService] no se pudo cargar el JSON de "${lang}"`, err);
      },
    });
  }

  /** Lista de idiomas soportados (para selectors UI). */
  get supportedLangs(): AppLang[] { return [...SUPPORTED]; }

  private readInitialLang(): AppLang {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored && SUPPORTED.includes(stored as AppLang)) return stored as AppLang;
    } catch { /* localStorage bloqueado */ }
    // Best-effort: si el browser pide es/en/pt, lo respetamos.
    const browser = (navigator.language || '').slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(browser as AppLang)) return browser as AppLang;
    return DEFAULT_LANG;
  }
}
